import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../Auth/AuthContext';
import { apiCall } from '../../utils/apiUtils';
import { showToast } from '../../utils/toastUtils';
import TicketGenerationCart from './TicketGenerationCart';
import SearchableDropdown from '../Common/SearchableDropdown';
import InvoiceCreateFilters from '../Common/InvoiceCreateFilters';
import PDFViewer from '../Common/PDFViewer';
import FilterDropdown from '../Common/FilterDropdown';
import Modal from '../Common/Modal';

const InvoiceManagement = () => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({ invoice_items: [], notes: '' });
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const { user } = useAuth();

  // Sub-menu state
  const [activeSubTab, setActiveSubTab] = useState('dashboard'); // 'dashboard' | 'manage' | 'create'

  // Dashboard stats state
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exporting, setExporting] = useState(false);

  // Create Purchase Order flow state
  const [vendors, setVendors] = useState([]);
  const [vendorSearch, setVendorSearch] = useState('');
  const [loadingVendors, setLoadingVendors] = useState(false);

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorOrders, setVendorOrders] = useState([]);
  const [loadingVendorOrders, setLoadingVendorOrders] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // PDF Viewer state
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [currentPDFUrl, setCurrentPDFUrl] = useState('');
  const [currentPDFTitle, setCurrentPDFTitle] = useState('');

  // Invoice Details Modal state
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [detailsInvoice, setDetailsInvoice] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null); // invoice ID being marked as paid

  // Filters for Create PO flow
  const [showFilters, setShowFilters] = useState(false);
  const [clients, setClients] = useState([]);
  const [states, setStates] = useState([]);
  const [poFilters, setPoFilters] = useState({
    client_name: '',
    state: '',
    amount_status: '' // '', 'full_pending', 'partial_pending'
  });
  const filterButtonRef = React.useRef(null);

  // Filters for Manage Purchase Orders tab
  const [showManageFilters, setShowManageFilters] = useState(false);
  const [manageFilters, setManageFilters] = useState({
    vendor_name: '',
    client_name: '',
    start_date: '',
    end_date: ''
  });
  const manageFilterButtonRef = useRef(null);
  const [manageVendors, setManageVendors] = useState([]);

  useEffect(() => {
    fetchInvoices();
  }, [page, pageSize]);

  useEffect(() => {
    if (activeSubTab === 'create') {
      fetchVendors(vendorSearch);
    }
  }, [activeSubTab, vendorSearch]);

  // Load clients and states when entering create tab
  useEffect(() => {
    if (activeSubTab === 'create') {
      fetchClients();
      fetchStates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  // Load vendors and clients for Manage tab filters
  useEffect(() => {
    if (activeSubTab === 'manage') {
      fetchManageVendors();
      fetchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  // Load stats when entering dashboard tab
  useEffect(() => {
    if (activeSubTab === 'dashboard') {
      fetchStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  const fetchManageVendors = async () => {
    try {
      const response = await apiCall('/api/vendors?limit=200');
      setManageVendors(response.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors for manage filters:', error);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const response = await apiCall('/api/invoices/stats');
      setStats(response);
    } catch (error) {
      console.error('Error fetching invoice stats:', error);
      showToast.error('Failed to fetch invoice statistics');
    } finally {
      setLoadingStats(false);
    }
  };

  const handleExport = async () => {
    if (!exportStartDate || !exportEndDate) {
      showToast.error('Please select both start and end dates');
      return;
    }
    try {
      setExporting(true);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/invoices/export?start_date=${exportStartDate}&end_date=${exportEndDate}`,
        { credentials: 'include' }
      );
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Export failed');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoices_${exportStartDate}_to_${exportEndDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showToast.success('Invoices exported successfully!');
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting invoices:', error);
      showToast.error(error.message || 'Failed to export invoices');
    } finally {
      setExporting(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pageSize.toString()
      });
      // Add manage filters if set
      if (manageFilters.vendor_name) params.append('vendor_name', manageFilters.vendor_name);
      if (manageFilters.client_name) params.append('client_name', manageFilters.client_name);
      if (manageFilters.start_date) params.append('start_date', manageFilters.start_date);
      if (manageFilters.end_date) params.append('end_date', manageFilters.end_date);

      const response = await apiCall(`/api/invoices?${params.toString()}`);
      setInvoices(response.invoices || []);
      setTotalCount(response.total_count || 0);
      setTotalPages(response.total_pages || Math.ceil(((response.total_count || 0)) / pageSize) || 1);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      showToast.error('Failed to fetch purchase orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchVendors = async (searchTerm = '') => {
    try {
      setLoadingVendors(true);
      const params = new URLSearchParams({
        page: '1',
        limit: '200' // Get more vendors for dropdown
      });
      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
      }
      const response = await apiCall(`/api/vendors?${params.toString()}`);
      setVendors(response.vendors || []);
    } catch (error) {
      console.error('Error fetching vendors:', error);
      showToast.error('Failed to fetch vendors');
    } finally {
      setLoadingVendors(false);
    }
  };

  const fetchOrdersForVendor = async (vendor, filterOverride) => {
    try {
      setLoadingVendorOrders(true);
      setSelectedVendor(vendor);
      setSelectedOrderIds([]);
      const activeFilters = filterOverride !== undefined ? filterOverride : poFilters;
      const params = new URLSearchParams();
      params.append('vendor_id', vendor.id);
      if (activeFilters.client_name) params.append('client_name', activeFilters.client_name);
      if (activeFilters.state) params.append('state', activeFilters.state);
      if (activeFilters.amount_status) params.append('amount_status', activeFilters.amount_status);
      const response = await apiCall(`/api/orders/available-for-invoice?${params.toString()}`);
      const orders = (response.orders || []).filter(o => (o.remaining_stamp_duty || 0) > 0);
      setVendorOrders(orders);
    } catch (error) {
      console.error('Error fetching vendor orders:', error);
      showToast.error('Failed to fetch orders for selected vendor');
    } finally {
      setLoadingVendorOrders(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await apiCall('/api/admin/clients');
      // OrderManagement expects an array of names; backend returns array
      setClients(response || []);
    } catch (error) {
      console.error('Error fetching clients for PO filters:', error);
    }
  };

  const fetchStates = async () => {
    try {
      const response = await apiCall('/api/states/from-orders');
      setStates(response.states || []);
    } catch (error) {
      console.error('Error fetching states for PO filters:', error);
    }
  };

  // Apply filters for Manage Purchase Orders tab
  const applyManageFilters = () => {
    setPage(1); // Reset to first page when filtering
    fetchInvoices();
    setShowManageFilters(false);
  };

  // Clear filters for Manage Purchase Orders tab
  const clearManageFilters = () => {
    setManageFilters({
      vendor_name: '',
      client_name: '',
      start_date: '',
      end_date: ''
    });
    setPage(1);
    // Trigger refetch with cleared filters
    setTimeout(() => fetchInvoices(), 0);
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => prev.includes(orderId) ? prev.filter(id => id !== orderId) : [...prev, orderId]);
  };

  const openCartWithSelected = () => {
    if (!selectedOrderIds.length) {
      showToast.error('Select at least one order');
      return;
    }
    setIsCartOpen(true);
  };

  const viewPDF = async (invoice) => {
    try {
      const pdfUrl = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/invoices/${invoice.id}/pdf`;

      // Test if PDF is accessible
      const response = await fetch(pdfUrl, {
        credentials: 'include'
      });

      if (response.ok) {
        setCurrentPDFUrl(pdfUrl);
        setCurrentPDFTitle(`Purchase Order - ${invoice.invoice_number}`);
        setShowPDFViewer(true);
      } else {
        showToast.error('PDF not available for this purchase order');
      }
    } catch (error) {
      console.error('Error checking PDF availability:', error);
      showToast.error('Failed to load PDF. Please try again.');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoiceNumber) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001'}/api/invoices/${invoiceId}/pdf`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `invoice_${invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        showToast.success('Purchase Order downloaded successfully');
      } else {
        throw new Error('Failed to download PDF');
      }
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showToast.error('Failed to download purchase order PDF');
    }
  };

  const handleMarkAsPaid = async (invoiceId, invoiceNumber) => {
    // Confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to mark invoice "${invoiceNumber}" as paid? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      setMarkingPaid(invoiceId);
      await apiCall(`/api/invoices/${invoiceId}/mark-paid`, {
        method: 'PATCH'
      });
      showToast.success('Invoice marked as paid successfully');
      fetchInvoices();
      // Refresh stats if on dashboard
      if (activeSubTab === 'dashboard') {
        fetchStats();
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      showToast.error('Failed to mark invoice as paid');
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleDeleteInvoice = async () => {
    try {
      if (deletingInvoice) return;
      setDeletingInvoice(true);
      await apiCall(`/api/invoices/${selectedInvoice.id}`, {
        method: 'DELETE'
      });
      showToast.success('Purchase Order deleted successfully');
      setShowDeleteModal(false);
      setSelectedInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error('Error deleting purchase order:', error);
      showToast.error('Failed to delete purchase order');
    } finally {
      setDeletingInvoice(false);
    }
  };

  const handleEditInvoice = async (invoice) => {
    try {
      setLoadingEdit(true);
      setSelectedInvoice(invoice);

      // Fetch available orders for the edit form
      const ordersResponse = await apiCall('/api/orders/available-for-invoice');
      const availableOrders = ordersResponse.orders || [];

      // Also fetch the specific orders that are already in this invoice
      const invoiceOrderIds = invoice.invoice_items.map(item => item.order_id);
      const invoiceOrdersResponse = await Promise.all(
        invoiceOrderIds.map(orderId =>
          apiCall(`/api/orders/${orderId}`).catch(() => null)
        )
      );

      // Combine available orders with invoice orders, removing duplicates
      const invoiceOrders = invoiceOrdersResponse
        .filter(response => response && response.order)
        .map(response => response.order);

      const allOrders = [...availableOrders];
      invoiceOrders.forEach(invoiceOrder => {
        if (!allOrders.find(order => order.id === invoiceOrder.id)) {
          allOrders.push(invoiceOrder);
        }
      });

      setAvailableOrders(allOrders);

      // Set up the edit form with current invoice data
      setEditFormData({
        invoice_items: invoice.invoice_items.map(item => ({
          order_id: String(item.order_id),
          quantity_paid: parseInt(item.quantity_paid),
          amount_paid: parseFloat(item.amount_paid)
        })),
        notes: invoice.notes || ''
      });

      setShowEditModal(true);
    } catch (error) {
      console.error('Error preparing edit form:', error);
      showToast.error('Failed to load edit form');
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleUpdateInvoice = async () => {
    try {
      setLoadingEdit(true);

      // Validate form data before sending
      if (!editFormData.invoice_items || editFormData.invoice_items.length === 0) {
        showToast.error('Invoice must have at least one item');
        return;
      }

      // Validate each item
      for (const item of editFormData.invoice_items) {
        if (!item.order_id || item.quantity_paid <= 0 || item.amount_paid <= 0) {
          showToast.error('All items must have valid order ID, quantity, and amount');
          return;
        }
      }

      console.log('Updating invoice with data:', JSON.stringify(editFormData, null, 2));

      // Ensure data is clean and serializable
      const cleanData = {
        invoice_items: editFormData.invoice_items.map(item => ({
          order_id: String(item.order_id),
          quantity_paid: parseInt(item.quantity_paid),
          amount_paid: parseFloat(item.amount_paid)
        })),
        notes: editFormData.notes || ''
      };

      console.log('Clean data being sent:', JSON.stringify(cleanData, null, 2));

      const response = await apiCall(`/api/invoices/${selectedInvoice.id}`, {
        method: 'PUT',
        data: cleanData
      });

      console.log('Update response:', response);

      showToast.success('Purchase Order updated successfully');
      setShowEditModal(false);
      setSelectedInvoice(null);
      setEditFormData({ invoice_items: [], notes: '' });
      fetchInvoices();
    } catch (error) {
      console.error('Error updating purchase order:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to update purchase order';
      showToast.error(errorMessage);
    } finally {
      setLoadingEdit(false);
    }
  };

  const handleRemoveItem = (indexToRemove) => {
    setEditFormData(prev => ({
      ...prev,
      invoice_items: prev.invoice_items.filter((_, index) => index !== indexToRemove)
    }));
  };

  const handleQuantityChange = (index, newQuantity) => {
    setEditFormData(prev => ({
      ...prev,
      invoice_items: prev.invoice_items.map((item, i) => {
        if (i === index) {
          // Calculate new amount based on quantity and denomination
          const order = availableOrders.find(o => o.id === item.order_id);
          if (order && order.denomination) {
            const quantity = parseInt(newQuantity) || 0;
            const amount = quantity * order.denomination;
            return {
              ...item,
              quantity_paid: quantity,
              amount_paid: parseFloat(amount.toFixed(2))
            };
          }
          return { ...item, quantity_paid: parseInt(newQuantity) || 0 };
        }
        return item;
      })
    }));
  };

  const getOrderDetails = (order_id) => {
    const order = availableOrders.find(o => o.id === order_id);
    return order || {};
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'generated': 'bg-blue-100 text-blue-800',
      'sent': 'bg-green-100 text-green-800',
      'paid': 'bg-emerald-100 text-emerald-800',
      'cancelled': 'bg-red-100 text-red-800'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const openDetailsModal = (invoice) => {
    setDetailsInvoice(invoice);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-menu for Invoices */}
      <div className="flex items-center justify-between">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveSubTab('dashboard')}
            className={`px-4 py-2 rounded-md text-sm ${activeSubTab === 'dashboard' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'} transition-colors`}
          >
            Dashboard
          </button>
          <button
            onClick={() => setActiveSubTab('manage')}
            className={`px-4 py-2 rounded-md text-sm ${activeSubTab === 'manage' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'} transition-colors`}
          >
            Manage Purchase Orders
          </button>
          <button
            onClick={() => setActiveSubTab('create')}
            className={`px-4 py-2 rounded-md text-sm ${activeSubTab === 'create' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'} transition-colors`}
          >
            Create Purchase Order
          </button>
        </div>
      </div>

      {/* Dashboard View */}
      {activeSubTab === 'dashboard' && (
        <div className="space-y-6">
          {/* Dashboard Header */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Financial Dashboard</h2>
              <p className="text-gray-600 mt-1">Overview of purchase order finances</p>
            </div>
            <button
              onClick={() => setShowExportModal(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export to Excel
            </button>
          </div>

          {loadingStats ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            </div>
          ) : stats ? (
            <>
              {/* Stat Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Invoices</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">{stats.total_invoices || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-full">
                      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Amount</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">{formatCurrency(stats.total_amount || 0)}</p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-full">
                      <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-emerald-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Paid Amount</p>
                      <p className="text-3xl font-bold text-emerald-600 mt-1">{formatCurrency(stats.paid_amount || 0)}</p>
                    </div>
                    <div className="p-3 bg-emerald-100 rounded-full">
                      <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-amber-500">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Pending Amount</p>
                      <p className="text-3xl font-bold text-amber-600 mt-1">{formatCurrency(stats.pending_amount || 0)}</p>
                    </div>
                    <div className="p-3 bg-amber-100 rounded-full">
                      <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Breakdown */}
              {stats.status_breakdown && Object.keys(stats.status_breakdown).length > 0 && (
                <div className="bg-white rounded-xl shadow-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Status Breakdown</h3>
                  <div className="flex flex-wrap gap-3">
                    {Object.entries(stats.status_breakdown).map(([status, count]) => (
                      <div key={status} className="flex items-center space-x-2 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                        {getStatusBadge(status)}
                        <span className="text-lg font-bold text-gray-700">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Invoices */}
              {stats.recent_invoices && stats.recent_invoices.length > 0 && (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Recent Purchase Orders</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {stats.recent_invoices.map((invoice) => (
                          <tr key={invoice.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {invoice.invoice_number}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {invoice.vendor_name || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {formatCurrency(invoice.total_amount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {getStatusBadge(invoice.status)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                              {formatDate(invoice.created_at)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p>No data available. Create some purchase orders to see statistics.</p>
            </div>
          )}
        </div>
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-gray-900">Export Invoices to Excel</h3>
              <button onClick={() => setShowExportModal(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={exportStartDate}
                  onChange={(e) => setExportStartDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={exportEndDate}
                  onChange={(e) => setExportEndDate(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowExportModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExport}
                  disabled={exporting || !exportStartDate || !exportEndDate}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {exporting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Exporting...
                    </>
                  ) : (
                    <>Download Excel</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Details Modal */}
      <Modal
        isOpen={showDetailsModal && !!detailsInvoice}
        onClose={() => setShowDetailsModal(false)}
        size="max-w-4xl"
      >
        {detailsInvoice && (
          <div className="h-full flex flex-col bg-white">
            {/* Modal Header */}
            <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
                aria-label="Back"
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h3 className="text-xl font-bold text-gray-900">Invoice Details</h3>
                <p className="text-sm text-gray-500">{detailsInvoice.invoice_number}</p>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-4">

              {/* Status Row with Edit/Delete on right */}
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-gray-600 font-medium">Status:</span>
                  {getStatusBadge(detailsInvoice.status)}
                </div>
                <div className="flex items-center space-x-2">
                  {/* Edit Button - only for admin/accountant and editable statuses */}
                  {(user.role === 'admin' || user.role === 'accountant') && detailsInvoice.status !== 'paid' && detailsInvoice.status !== 'cancelled' && (
                    <button
                      onClick={() => {
                        setShowDetailsModal(false);
                        handleEditInvoice(detailsInvoice);
                      }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors border border-green-200"
                      disabled={loadingEdit}
                      title="Edit Invoice"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                  )}
                  {/* Delete Button - only for admin */}
                  {user.role === 'admin' && detailsInvoice.status !== 'cancelled' && (
                    <button
                      onClick={() => {
                        setSelectedInvoice(detailsInvoice);
                        setShowDetailsModal(false);
                        setShowDeleteModal(true);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-red-200"
                      title="Delete Invoice"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1 1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>

              {/* Main Info Grid */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Vendor</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{detailsInvoice.vendor_name || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Payer Email</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{detailsInvoice.payer_email || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Created By</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{detailsInvoice.accountant_name || detailsInvoice.accountant_email || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 uppercase tracking-wider">Created Date</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{formatDate(detailsInvoice.created_at)}</p>
                </div>
              </div>

              {/* Total Amount */}
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 border border-blue-100">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 font-medium">Total Amount</span>
                  <span className="text-xl font-bold text-indigo-600">{formatCurrency(detailsInvoice.total_amount)}</span>
                </div>
              </div>

              {/* Invoice Items */}
              {detailsInvoice.invoice_items && detailsInvoice.invoice_items.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Invoice Items ({detailsInvoice.invoice_items.length})</h4>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Denomination</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount Paid</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {detailsInvoice.invoice_items.map((item, idx) => (
                          <tr key={idx} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-900">{item.order_number || item.order_id || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-600">{item.client_name || 'N/A'}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">{formatCurrency(item.denomination || 0)}</td>
                            <td className="px-3 py-2 text-sm text-gray-900 text-right">{item.quantity_paid || 0}</td>
                            <td className="px-3 py-2 text-sm font-medium text-gray-900 text-right">{formatCurrency(item.amount_paid || 0)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Notes */}
              {detailsInvoice.notes && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-2">Notes</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{detailsInvoice.notes}</p>
                </div>
              )}
            </div>

            {/* Modal Footer - Actions */}
            <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex justify-between items-center flex-shrink-0">
              {/* Left: PDF Actions */}
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    viewPDF(detailsInvoice);
                  }}
                  className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  title="View PDF in browser"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View PDF
                </button>
                <button
                  onClick={() => handleDownloadPDF(detailsInvoice.id, detailsInvoice.invoice_number)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  title="Download PDF to your device"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download
                </button>
              </div>
              {/* Right: Close Button */}
              <button
                onClick={() => setShowDetailsModal(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {activeSubTab === 'manage' && (
        <div className="space-y-4">
          {/* Header - Only visible in Manage tab */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Purchase Orders Management</h2>
              <p className="text-gray-600 mt-1">Manage stamp duty purchase orders</p>
            </div>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              {/* Filter Button */}
              <div className="relative">
                <button
                  ref={manageFilterButtonRef}
                  onClick={() => setShowManageFilters(!showManageFilters)}
                  className="flex items-center px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707v4.172a1 1 0 01-.464.864l-3.5 2.333A1 1 0 019 20.586V15.414a1 1 0 00-.293-.707L2.293 8.293A1 1 0 012 7.586V4z" />
                  </svg>
                  Filters
                </button>
                <FilterDropdown
                  isOpen={showManageFilters}
                  onClose={() => setShowManageFilters(false)}
                  filters={manageFilters}
                  setFilters={setManageFilters}
                  onApplyFilters={applyManageFilters}
                  buttonRef={manageFilterButtonRef}
                  title="Filter Purchase Orders"
                  showVendor={true}
                  showClient={true}
                  showDates={false}
                  vendors={manageVendors}
                  clients={clients}
                />
              </div>
              {/* Clear Filters Button - only show when filters are active */}
              {(manageFilters.vendor_name || manageFilters.client_name) && (
                <button
                  onClick={clearManageFilters}
                  className="flex items-center px-3 py-2 text-sm text-red-600 border border-red-300 rounded-md hover:bg-red-50 transition-colors"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear
                </button>
              )}
              <div>
                Total Purchase Orders: {totalCount}
              </div>
              <div className="flex items-center space-x-2">
                <span>Rows per page:</span>
                <select
                  value={pageSize}
                  onChange={(e) => { setPage(1); setPageSize(parseInt(e.target.value) || 10); }}
                  className="border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* Active filter chips */}
          {(manageFilters.vendor_name || manageFilters.client_name) && (
            <div className="flex flex-wrap gap-2">
              {manageFilters.vendor_name && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                  <span className="mr-1 font-medium">Vendor:</span>
                  <span className="mr-1">{manageFilters.vendor_name}</span>
                  <button onClick={() => setManageFilters(prev => ({ ...prev, vendor_name: '' }))} className="text-blue-700 hover:text-blue-900">×</button>
                </span>
              )}
              {manageFilters.client_name && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 border border-blue-200">
                  <span className="mr-1 font-medium">Client:</span>
                  <span className="mr-1">{manageFilters.client_name}</span>
                  <button onClick={() => setManageFilters(prev => ({ ...prev, client_name: '' }))} className="text-blue-700 hover:text-blue-900">×</button>
                </span>
              )}
            </div>
          )}

          <div className="bg-white shadow-lg rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
                  <div className="flex-1 flex items-center justify-between sm:hidden">
                    <button
                      onClick={() => setPage(page - 1)}
                      disabled={page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-700">
                      Showing {(totalCount === 0) ? 0 : ((page - 1) * pageSize + 1)}–{Math.min(page * pageSize, totalCount)} of {totalCount}
                    </span>
                    <button
                      onClick={() => setPage(page + 1)}
                      disabled={page >= totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{(totalCount === 0) ? 0 : ((page - 1) * pageSize + 1)}</span>
                        –
                        <span className="font-medium">{Math.min(page * pageSize, totalCount)}</span> of{' '}
                        <span className="font-medium">{totalCount}</span>
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                        <button
                          onClick={() => setPage(page - 1)}
                          disabled={page <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setPage(page + 1)}
                          disabled={page >= totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Purchase Order ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Vendor
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Accountant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created Date
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                        <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg font-medium">No purchase orders found</p>
                        <p className="text-sm text-gray-400 mt-1">Start by creating purchase orders from the Orders tab</p>
                      </td>
                    </tr>
                  ) : (
                    invoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetailsModal(invoice)}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {invoice.invoice_number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {invoice.vendor_name || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {invoice.accountant_email || 'N/A'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatCurrency(invoice.total_amount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {formatDate(invoice.created_at)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-2">
                            {/* View Details Button */}
                            <button
                              onClick={(e) => { e.stopPropagation(); openDetailsModal(invoice); }}
                              className="text-indigo-600 hover:text-indigo-900 transition-colors"
                              title="View Details"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </button>
                            {/* Mark as Paid button - only for non-paid, non-cancelled invoices */}
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleMarkAsPaid(invoice.id, invoice.invoice_number); }}
                                className={`transition-colors ${markingPaid === invoice.id ? 'text-gray-400' : 'text-emerald-600 hover:text-emerald-900'}`}
                                disabled={markingPaid === invoice.id}
                                title="Mark as Paid"
                              >
                                {markingPaid === invoice.id ? (
                                  <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {activeSubTab === 'create' && (
        <div className="space-y-6">
          {/* Create PO Header */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create Purchase Order</h2>
            <p className="text-gray-600 mt-1">Generate a new purchase order for stamp duty payments</p>
          </div>

          {/* Vendor Selector */}
          <div className="bg-white shadow-lg rounded-lg p-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Vendor
              </label>
              <SearchableDropdown
                options={vendors}
                value={selectedVendor}
                onChange={(vendor) => fetchOrdersForVendor(vendor)}
                placeholder="Search vendors by name or email..."
                loading={loadingVendors}
                searchValue={vendorSearch}
                onSearchChange={setVendorSearch}
                displayKey="name"
                valueKey="id"
                className="w-full"
              />
            </div>
            {selectedVendor && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900">
                      Selected: {selectedVendor.name}
                    </p>
                    {selectedVendor.email && (
                      <p className="text-xs text-blue-700">{selectedVendor.email}</p>
                    )}
                  </div>
                  <button
                    onClick={() => { setSelectedVendor(null); setVendorOrders([]); setSelectedOrderIds([]); }}
                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    Change Vendor
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Orders For Selected Vendor */}
          {selectedVendor && (
            <div className="bg-white shadow-lg rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Orders for {selectedVendor.name}</h3>
                  <p className="text-sm text-gray-600">Select orders to include in the purchase order</p>
                </div>
                <div className="flex items-center space-x-2">
                  {/* Clear Filters Button - only show when filters are applied */}
                  {(poFilters.client_name || poFilters.state || poFilters.amount_status) && (
                    <button
                      onClick={() => {
                        const clearedFilters = {
                          client_name: '',
                          state: '',
                          amount_status: ''
                        };
                        setPoFilters(clearedFilters);
                        if (selectedVendor) {
                          fetchOrdersForVendor(selectedVendor, clearedFilters);
                        }
                      }}
                      className="px-3 py-2 border border-red-300 rounded-md text-red-700 hover:bg-red-50 text-sm"
                    >
                      Clear Filters
                    </button>
                  )}
                  <div className="relative">
                    <button
                      ref={filterButtonRef}
                      onClick={() => setShowFilters(!showFilters)}
                      className="px-3 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                    >
                      Filters
                    </button>
                    <InvoiceCreateFilters
                      isOpen={showFilters}
                      onClose={() => setShowFilters(false)}
                      filters={poFilters}
                      setFilters={setPoFilters}
                      clients={clients}
                      states={states}
                      onApplyFilters={() => {
                        setShowFilters(false);
                        if (selectedVendor) {
                          fetchOrdersForVendor(selectedVendor);
                        }
                      }}
                      buttonRef={filterButtonRef}
                    />
                  </div>
                  <button
                    onClick={() => { setSelectedVendor(null); setVendorOrders([]); setSelectedOrderIds([]); }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Change Vendor
                  </button>
                  <button
                    onClick={openCartWithSelected}
                    disabled={selectedOrderIds.length === 0}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>

              {loadingVendorOrders ? (
                <div className="py-12 text-center text-gray-500">Loading orders...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2">
                          <input
                            type="checkbox"
                            checked={vendorOrders.length > 0 && selectedOrderIds.length === vendorOrders.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedOrderIds(vendorOrders.map(o => o.id));
                              } else {
                                setSelectedOrderIds([]);
                              }
                            }}
                          />
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Name</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Party</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">State</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Denomination</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty Paid / Total Qty</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining / Total Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {vendorOrders.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No unpaid orders found for this vendor</td>
                        </tr>
                      ) : (
                        vendorOrders.map(order => (
                          <tr key={order.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                              <input
                                type="checkbox"
                                checked={selectedOrderIds.includes(order.id)}
                                onChange={() => toggleOrderSelection(order.id)}
                              />
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-900">{order.order_number}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{order.client_name || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{(order.first_party && order.first_party.name) || order.first_party_name || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{order.state || 'N/A'}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">₹{order.denomination}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">{Math.round(((order.total_stamp_duty - order.remaining_stamp_duty) / (order.denomination || 1)))} / {order.quantity}</td>
                            <td className="px-4 py-2 text-sm text-gray-900">₹{order.remaining_stamp_duty} / ₹{order.total_stamp_duty}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Cart */}
          {isCartOpen && (
            <TicketGenerationCart
              isOpen={isCartOpen}
              onClose={() => setIsCartOpen(false)}
              selectedOrders={vendorOrders.filter(o => selectedOrderIds.includes(o.id))}
              onInvoiceGenerated={() => {
                setIsCartOpen(false);
                setSelectedOrderIds([]);
                setSelectedVendor(null);
                setVendorOrders([]);
                setActiveSubTab('manage');
                fetchInvoices();
              }}
            />
          )}
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <svg className="w-8 h-8 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.864-.833-2.634 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="text-lg font-semibold text-gray-900">Delete IPurchase Order</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete purchase order <strong>{selectedInvoice?.invoice_number}</strong>?
                This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedInvoice(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteInvoice}
                  disabled={deletingInvoice}
                  className={`px-4 py-2 bg-red-600 text-white rounded-md transition-colors ${deletingInvoice ? 'opacity-60 cursor-not-allowed' : 'hover:bg-red-700'}`}
                >
                  {deletingInvoice ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Invoice Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-900">
                  Edit Purchase Order: {selectedInvoice?.invoice_number}
                </h3>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedInvoice(null);
                    setEditFormData({ invoice_items: [], notes: '' });
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Invoice Items */}
              <div className="mb-6">
                <h4 className="text-lg font-medium text-gray-900 mb-4">Invoice Items</h4>
                {editFormData.invoice_items.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <p>No items in invoice</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {editFormData.invoice_items.map((item, index) => {
                      const orderDetails = getOrderDetails(item.order_id);
                      return (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">
                                Order ID: {item.order_id}
                              </h5>
                              <p className="text-sm text-gray-600">
                                Client: {orderDetails.client_name || 'N/A'} |
                                Article: {orderDetails.article_name || 'N/A'} |
                                Denomination: ₹{orderDetails.denomination || 'N/A'}
                              </p>
                            </div>
                            <button
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-600 hover:text-red-800 transition-colors"
                              title="Remove Item"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Quantity
                              </label>
                              <input
                                type="number"
                                min="1"
                                value={item.quantity_paid}
                                onChange={(e) => handleQuantityChange(index, e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Amount
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                value={item.amount_paid}
                                readOnly
                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Remaining Unpaid Quantity
                              </label>
                              <p className="px-3 py-2 text-sm text-gray-600">
                                {orderDetails.remaining_stamp_duty ?
                                  Math.ceil(orderDetails.remaining_stamp_duty / (orderDetails.denomination || 1)) :
                                  'N/A'
                                }
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes
                </label>
                <textarea
                  value={editFormData.notes}
                  onChange={(e) => setEditFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Add any notes for this invoice..."
                />
              </div>

              {/* Total Amount */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-medium text-gray-900">Total Amount:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency(editFormData.invoice_items.reduce((sum, item) => sum + item.amount_paid, 0))}
                  </span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedInvoice(null);
                    setEditFormData({ invoice_items: [], notes: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={loadingEdit}
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateInvoice}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                  disabled={loadingEdit || editFormData.invoice_items.length === 0}
                >
                  {loadingEdit && (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  <span>Update Purchase Order</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PDF Viewer */}
      <PDFViewer
        isOpen={showPDFViewer}
        onClose={() => {
          setShowPDFViewer(false);
          setCurrentPDFUrl('');
          setCurrentPDFTitle('');
        }}
        pdfUrl={currentPDFUrl}
        title={currentPDFTitle}
        token={localStorage.getItem('token')}
      />
    </div>
  );
};

export default InvoiceManagement;